import type { ContextType, ReactNode } from "react";
import { PureComponent } from "react";

import classNames from "classnames";

import { IconButton, ScrollArea, findParentByClass, StructureModal, DotsLoader } from "@similarweb/ui-components";

import { SearchTrackerFromKeywordsTable } from "app/components/campaigns/searchTrackerFromKeywordsTable/SearchTrackerFromKeywordsTable";
import { AutocompleteStyled } from "app/components/campaigns/searchTrackerFromKeywordsTable/SearchTrackerFromKeywordsTableStyle";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { HOOKS_ICON } from "app/constants/hooks";
import { translate as fTranslate } from "app/filters/i18nFilter";
import { getIsRTCreateCampaignsAvailable } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import { emptyKeywordGroup } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { EHooksVersions } from "app/services/SwHook";
import { BaseFlex } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TranslationContext } from "../../WithTranslation/src/TranslationProvider";

import { ModalTableSelectionRemoveKeywords } from "./ModalTableSelectionRemoveKeywords";
import { ADD_ICON, DELETE_ICON, NO_DATA, proModalStyles } from "./TableSelectionGroupKeywordConst";
import {
    AddIconButton,
    DropDownItem,
    NoDataWrapper,
    NoResultsWrapper,
    SelectorContainer,
    SubTitle,
    WarningMessageWrapper,
} from "./TableSelectionGroupKeywordStyle";
import type {
    ITableSelectionNewGroupDropdownProps,
    ITableSelectionNewGroupDropdownState,
} from "./TableSelectionGroupKeywordTypes";
import { ETableSelectionNewGroupDropdownMode, ICampaignType } from "./TableSelectionGroupKeywordTypes";
import { TableSelectionKeywordsWarning } from "./TableSelectionKeywordsWarning";
import { TextfieldStyled } from "./TextfieldStyled";
import { allTrackers } from "app/services/track/track";

const ScrollAreaWrap = ({ children, ...rest }: { children: ReactNode; [key: string]: unknown }) => {
    return (
        <ScrollArea
            style={{
                maxHeight: 200,
                minHeight: 0,
            }}
            verticalScrollbarStyle={{ borderRadius: 5 }}
            horizontal={false}
            smoothScrolling={true}
            minScrollSize={48}
            {...rest}
        >
            {children}
        </ScrollArea>
    );
};

export const generalGroupToastMessage = (groupName: string, numberOfKeywords: number) => {
    return fTranslate(
        numberOfKeywords === 1
            ? "table.selection.keyword.list.groupupdated"
            : "table.selection.keywords.list.groupupdated",
        {
            count: numberOfKeywords.toString(),
            list_name: groupName,
        },
    );
};

export class TableSelectionGroupKeywordComponent extends PureComponent<
    ITableSelectionNewGroupDropdownProps,
    ITableSelectionNewGroupDropdownState
> {
    static contextType = TranslationContext;

    context: ContextType<typeof TranslationContext>;

    // @ts-ignore FIXME: strict migration - autocompleteRef implicitly has 'any' type — complex popup ref type from ui-components
    private autocompleteRef;

    public static defaultProps = {
        mode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
        count: 0,
        appendTo: ".swReactTable-header-wrapper",
        trackingCategory: "",
        allItemsExistsMessage: "",
        maxItemsMessage: "",
        hasCreateButton: true,
        hasRemoveButton: false,
        shouldAddNewItemToList: true,
        onRemoveKeywordsFromList: () => undefined,
    };

    public state = {
        groupName: "",
        isAutocompleteOpen: false,
        isKeywordRemovalModalOpen: false,
        isWarningOpen: false,
        group: emptyKeywordGroup,
        campaigns: [],
        campaignType: ICampaignType.BrandProtection,
        displayCampaigns: false,
        isLoading: false,
    };

    public componentDidMount() {
        document.body.addEventListener("click", this.handleBodyClick, { capture: true });
    }

    public componentWillUnmount() {
        document.body.removeEventListener("click", this.handleBodyClick, { capture: true });
    }

    // we want the autocomplete and warning messages to close when clicking outside of themselves
    private handleBodyClick = (e: FixmeType) => {
        if (
            !findParentByClass(e.target, "AutocompleteKeywordGroupsAddToList") &&
            !findParentByClass(e.target, "TableSelectionRemoveKeywords") &&
            !findParentByClass(e.target, "TableSelectionKeywordsWarning")
        ) {
            this.setState({
                isAutocompleteOpen: false,
                isWarningOpen: false,
                isKeywordRemovalModalOpen: false,
            });
        }
    };

    private getAutocompleteData = (query: string) => {
        const translate = this.context?.translate;
        if (!query) {
            return this.props.groups;
        } else {
            const normalizedQuery = query.trim().toLowerCase();
            const filteredGroups = this.props.groups.filter((group) =>
                group.text.trim().toLowerCase().includes(normalizedQuery),
            );
            return filteredGroups.length
                ? filteredGroups
                : [NO_DATA, <NoDataWrapper key="no-data">{translate?.("table.selection.add.no.data")}</NoDataWrapper>];
        }
    };

    private isArrayOfStrings = (array: any[]): array is string[] => {
        return array.every((item) => typeof item === "string");
    };

    public render() {
        const translate = this.context?.translate;
        const selectedRows =
            this.props.selectedRows &&
            (this.isArrayOfStrings(this.props.selectedRows)
                ? this.props.selectedRows
                : this.props.selectedRows.map((item) => item.keyword));

        const isDisabled = selectedRows?.length === 0 || !selectedRows;
        const actionButtonsType = isDisabled ? "flat" : "invertedTransparent";
        const isRTCreateCampaignsAllowed = getIsRTCreateCampaignsAvailable();
        return (
            <BaseFlex>
                {this.props.hasRemoveButton ? (
                    <div>
                        <IconButton
                            onClick={this.onRemoveKeywordsFromGroup}
                            type={actionButtonsType}
                            isDisabled={isDisabled}
                            iconName={DELETE_ICON}
                            iconSize="xs"
                            className={"DropdownContent-container.transparent-background-dropdown-container"}
                        >
                            {translate?.("table.selection.remove.from.list")}
                        </IconButton>
                    </div>
                ) : null}

                {this.props.groups.length > 0 ? (
                    <div style={{ marginLeft: 8, marginRight: 8 }}>
                        <AutocompleteStyled
                            className="AutocompleteKeywordGroupsAddToList"
                            getListItems={this.getAutocompleteData}
                            renderItems={this.autocompleteRenderItems}
                            loadingComponent={<DotsLoader />}
                            autoFocus={true}
                            floating={true}
                            debounce={250}
                            preventTruncateUnlessForced={true}
                            disabled={isDisabled}
                            actionButtonsType={actionButtonsType}
                            closePopup={this.close}
                            onCancelPopup={this.onCancel}
                            placeholder={translate?.("table.selection.autocomplete.placeholder") ?? ""}
                            data-automation="add-keyword-autocomplete-with-add-button"
                            shouldAddNewItemToList={this.props.shouldAddNewItemToList}
                            onAddNewItemToListClick={this.props.onNewListButtonClick}
                            addNewItemToListText={translate?.("table.selection.create.group") ?? ""}
                            addPopUpOption={true}
                            popupConfig={{
                                height: this.props.hasCreateButton ? 308 : 253,
                                width: 360,
                                placement: "bottom-left",
                                cssClassContainer: classNames(
                                    "DropdownContent-container",
                                    "Popup-content-for-group-list-dropdown",
                                    "Popup-content--pro-dropdown",
                                ),
                                defaultOpen: false,
                                enabled: !isDisabled,
                            }}
                            popupButton={
                                <IconButton
                                    type={actionButtonsType}
                                    iconName={ADD_ICON}
                                    iconSize="xs"
                                    onClick={this.onAddToListButtonClick}
                                    isDisabled={isDisabled}
                                >
                                    {translate?.("table.selection.keyword.list")}
                                </IconButton>
                            }
                            popupRef={(ref: FixmeType) => (this.autocompleteRef = ref)}
                        />
                    </div>
                ) : (
                    <AddIconButton
                        type={actionButtonsType}
                        isDisabled={isDisabled}
                        iconName={ADD_ICON}
                        iconSize="xs"
                        onClick={this.props.onNewListButtonClick}
                    >
                        {translate?.("table.selection.create.group")}
                    </AddIconButton>
                )}
                {!isRTCreateCampaignsAllowed ? (
                    <IconButton
                        type={actionButtonsType}
                        onClick={() =>
                            onHookClick(
                                "RankTracker_CreateFromKeywordsResearch",
                                EPrimarySideBar3StateType.RankTracker_home,
                                EHooksVersions.V5,
                            )
                        }
                        isDisabled={isDisabled}
                        iconName={HOOKS_ICON}
                    >
                        {translate?.("table.selection.search.tracker")}
                    </IconButton>
                ) : (
                    <SearchTrackerFromKeywordsTable selectedRows={selectedRows ?? []}>
                        <IconButton
                            type={actionButtonsType}
                            iconName={ADD_ICON}
                            iconSize="xs"
                            onClick={this.onSearchTrackerClick}
                            isDisabled={isDisabled}
                        >
                            {translate?.("table.selection.search.tracker")}
                        </IconButton>
                    </SearchTrackerFromKeywordsTable>
                )}

                {this.state.isWarningOpen ? (
                    <WarningMessageWrapper className="TableSelectionKeywordsWarning">
                        {this.getWarningItems()}
                    </WarningMessageWrapper>
                ) : null}
                <ModalTableSelectionRemoveKeywords
                    isOpen={this.state.isKeywordRemovalModalOpen}
                    numberOfKeywordsToRemove={this.props.count ?? 0}
                    onDelete={this.props.onRemoveKeywordsFromList ?? (() => undefined)}
                    onClose={() => {
                        this.setState({ isKeywordRemovalModalOpen: false });
                    }}
                />
                <StructureModal
                    isOpen={this.props.isCreateNewListModalOpen ?? false}
                    title={translate?.("table.selection.newgroup.keywords.create") ?? ""}
                    customStyles={proModalStyles}
                    showHeaderDivider={false}
                    mainButton={{
                        label: translate?.("table.selection.newgroup.confirm") ?? "",
                        onClick: this.onSubmit,
                        type: "primary",
                        tooltipEnabled: false,
                        isLoading: this.props.isLoading,
                        isDisabled: this.state.groupName.length === 0,
                    }}
                    secondaryButton={{
                        label: translate?.("table.selection.newgroup.cancel") ?? "",
                        onClick: this.onCancel,
                        type: "flatDark",
                        tooltipEnabled: false,
                    }}
                >
                    <SelectorContainer>
                        <SubTitle>{translate?.(this.props.newGroupNameLabel ?? "")}</SubTitle>
                        <TextfieldStyled
                            error={this.props.error}
                            errorMessage={this.props.errorMessage}
                            placeholder={translate?.("table.selection.newgroup.keywords.placeholder") ?? ""}
                            onChange={this.onGroupNameChange}
                            dataAutomation="group-name-text-field"
                        />
                    </SelectorContainer>
                </StructureModal>
            </BaseFlex>
        );
    }

    private onRemoveKeywordsFromGroup = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Keyword List Edit/Remove keyword button clicked");
        this.setState({ isKeywordRemovalModalOpen: true });
    };

    private onAddToListButtonClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Keyword List ");
        this.setState({
            isAutocompleteOpen: true,
        });
    };

    private onSearchTrackerClick = () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Search tracker/${!this.state.isAutocompleteOpen ? "Open" : "Close"}`,
        );
        this.setState({
            isAutocompleteOpen: true,
        });
    };

    private getWarningItems = () => {
        const translate = this.context?.translate;
        // we decide what to display based on the current mode ie. used max items/ items exist in group
        if (this.props.mode === ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS) {
            return [
                <TableSelectionKeywordsWarning
                    key="TableSelectionKeywordsWarning"
                    onCancel={this.onCancel}
                    message={translate?.(this.props.allItemsExistsMessage ?? "") ?? ""}
                />,
            ];
        }
        return [
            <TableSelectionKeywordsWarning
                key="TableSelectionKeywordsWarning"
                onCancel={this.onCancel}
                message={
                    translate?.(this.props.maxItemsMessage ?? "", {
                        count: this.props.count?.toString() ?? "0",
                    }) ?? ""
                }
            />,
        ];
    };

    private autocompleteRenderItems = ({ listItems }: FixmeType) => {
        const translate = this.context?.translate;

        if (!listItems.length && !this.state.isLoading) {
            return (
                <NoResultsWrapper>{translate?.("table.selection.custom.industry.list.no.results")}</NoResultsWrapper>
            );
        }
        const componentListItems = listItems[0] === NO_DATA ? listItems[1] : listItems.map(this.itemCreator);

        return <ScrollAreaWrap>{componentListItems}</ScrollAreaWrap>;
    };

    private itemCreator = (item: FixmeType, index: number) => {
        return (
            <DropDownItem
                key={`item-${index}`}
                id={item.id}
                iconName={this.props.groupIconName as any}
                iconSize="sm"
                onClick={this.onItemClick(item)}
            >
                {item.text}
            </DropDownItem>
        );
    };

    private onSubmit = () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Keyword List/Save/${this.state.groupName || ""}/${(this.props.selectedRows?.length?.toString() ?? "0") || ""}`,
        );
        this.props.onSubmit?.(this.state.groupName);
    };

    private onItemClick = (item: FixmeType) => () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Add to Keyword List/Save/${item.text || ""}/${(this.props.selectedRows?.length?.toString() ?? "0") || ""}`,
        );
        this.autocompleteRef.closePopup();
        this.props.onGroupClick?.(item);
    };

    private onGroupNameChange = (value: string) => {
        this.setState({
            groupName: value,
        });
    };

    public openWarning = () => {
        this.setState({ isWarningOpen: true, isAutocompleteOpen: false });
    };

    public close = () => {
        this.setState({ isAutocompleteOpen: false });
    };

    private onCancel = () => {
        this.setState(
            {
                groupName: "",
                isWarningOpen: false,
                isAutocompleteOpen:
                    this.props.mode === ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS &&
                    !this.props.isCreateNewListModalOpen,
            },
            () => {
                this.props.onCancel?.();
            },
        );
    };
}
